/**
 * Müşteri ↔ firma köprüsü (arayüz).
 *
 * Ofis bir davet kodu üretir, müşteriye verir; müşteri kendi hesabında kodu
 * girer, KİMİN davet ettiğini görür ve onaylar.
 *
 * Neden iki aşama: kod ele geçse bile (ekran görüntüsü, iletilen mesaj)
 * bağlantıyı kuran taraf müşteridir. Kendi hesabına girip onaylaması gerekir.
 * Ofis kimseyi kendi başına kendine bağlayamaz.
 *
 * Buradaki hiçbir kontrol güvenlik değildir. Kod doğrulama, tek kullanım,
 * süre ve yetki tamamen veritabanındaki SECURITY DEFINER fonksiyonlarda;
 * müşteri davet tablosunu okuyamaz bile. Bu dosya yalnızca ekranı çizer.
 *
 * KALDIRMAK İÇİN: bu klasörü sil, shell/index.ts listesinden satırı çıkar,
 * index.html'deki #fl-firm-box ve #fl-cust-box kaplarını kaldır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS } from './texts.ts';
import { drawQr, inviteUrl, takeHashInvite } from './qr.ts';

interface RpcResult {
  ok?: boolean; error?: string; code?: string;
  firm_name?: string; link_id?: string;
}

/** Önizlemesi yapılmış, onay bekleyen kod. */
let pending: string | null = null;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function t(k: string): string { return legacy.t(k); }

async function rpc(fn: string, args: Record<string, unknown>): Promise<RpcResult> {
  const sb = legacy.sb;
  if (!sb) return { ok: false, error: t('fl_nolink') };
  try {
    const r = await sb.rpc(fn, args);
    if (r && r.error) return { ok: false, error: String(r.error.message ?? r.error) };
    return (r && r.data) || { ok: false };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

function fail(hostId: string, msg: string): void {
  const host = el(hostId);
  if (host) host.innerHTML = '<div style="color:var(--red);font-size:12px">' + esc(msg) + '</div>';
}

/* ── Ofis tarafı ──────────────────────────────────────────────── */

async function createInvite(): Promise<void> {
  const out = el('fl-firm-out');
  if (out) out.innerHTML = '<div style="font-size:12px;color:var(--t2)">⏳</div>';
  const r = await rpc('create_firm_invite', { p_label: '' });
  if (!r.ok || !r.code) { fail('fl-firm-out', r.error ?? ''); return; }
  if (out) {
    out.innerHTML =
      '<div style="background:var(--c2);border:1px solid var(--div);border-radius:12px;padding:14px;text-align:center">'
      + '<div style="font-size:24px;font-weight:800;letter-spacing:2px;font-family:monospace;color:#D4A017">' + esc(r.code) + '</div>'
      + '<div style="font-size:10px;color:var(--t2);margin-top:6px">' + esc(t('fl_code_hint')) + '</div>'
      + '<div style="display:flex;gap:8px;justify-content:center;margin-top:10px">'
      + '<button class="btn bg bsm" onclick="flCopy(\'' + esc(r.code) + '\')">' + esc(t('fl_copy')) + '</button>'
      + '<button class="btn bg bsm" onclick="flShowQr(\'' + esc(r.code) + '\')">' + esc(t('fl_qr')) + '</button>'
      + '</div><div id="fl-qr" style="margin-top:10px"></div></div>';
  }
  void renderLinks();
}

function copy(code: string): void {
  try { navigator.clipboard.writeText(code); legacy.toast(t('fl_copied')); }
  catch { legacy.toast(code); }
}

async function showQr(code: string): Promise<void> {
  const box = el('fl-qr');
  if (!box) return;
  box.innerHTML = '<div style="font-size:12px;color:var(--t2)">⏳</div>';
  box.innerHTML = '<div id="fl-qr-canvas" style="display:inline-block;background:#fff;padding:10px;border-radius:10px"></div>';
  const host = el('fl-qr-canvas');
  const ok = host ? await drawQr(host, code) : false;
  if (!ok) { box.innerHTML = '<div style="font-size:11px;color:var(--t2)">' + esc(t('fl_qr_fail')) + '</div>'; return; }
  /* insertAdjacentHTML, innerHTML += DEĞİL. İkincisi kutunun tamamını metne
     çevirip yeniden ayrıştırır, ve bir <canvas>'ın bitmap'i o metnin parçası
     değildir — karekod siliniyordu. Ölçüldü: 14.287 siyah piksel, işlemden
     sonra 0 (canvas tamamen boş). insertAdjacentHTML yalnızca eklenen parçayı
     ayrıştırır, mevcut DOM'a dokunmaz; aynı ölçüm 14.287'de kalıyor. */
  box.insertAdjacentHTML('beforeend',
    '<div style="font-size:10px;color:var(--t2);margin-top:6px">' + esc(t('fl_qr_hint')) + '</div>');
}

/* ── Müşteri tarafı: iki aşama ────────────────────────────────── */

async function preview(): Promise<void> {
  const inp = el('fl-code-inp') as HTMLInputElement | null;
  const out = el('fl-cust-out');
  if (!inp) return;
  const code = (inp.value || '').trim();
  if (!code) return;
  if (out) out.innerHTML = '<div style="font-size:12px;color:var(--t2)">⏳</div>';
  const r = await rpc('preview_firm_invite', { p_code: code });
  if (!r.ok) { fail('fl-cust-out', r.error ?? ''); return; }
  pending = r.code ?? null;
  /* İkinci aşama: bağlanmadan ÖNCE kimin davet ettiğini ve neyin
     paylaşılacağını göster. Onay olmadan hiçbir şey kurulmaz. */
  if (out) {
    out.innerHTML =
      '<div style="background:var(--c2);border:1px solid var(--div);border-radius:12px;padding:14px">'
      + '<div style="font-size:14px;font-weight:700;margin-bottom:4px">' + esc(r.firm_name) + '</div>'
      + '<div style="font-size:12px;color:var(--t2);margin-bottom:8px">' + esc(t('fl_asks')) + '</div>'
      + '<div style="font-size:11px;color:var(--t2);line-height:1.6;margin-bottom:12px">' + esc(t('fl_will')) + '</div>'
      + '<div style="display:flex;gap:8px">'
      + '<button class="btn br" style="flex:1" onclick="flAccept()">' + esc(t('fl_confirm')) + '</button>'
      + '<button class="btn bg" onclick="flCancel()">' + esc(t('fl_cancel')) + '</button>'
      + '</div></div>';
  }
}

function cancel(): void {
  pending = null;
  const out = el('fl-cust-out'); if (out) out.innerHTML = '';
  const inp = el('fl-code-inp') as HTMLInputElement | null; if (inp) inp.value = '';
}

async function accept(): Promise<void> {
  if (!pending) return;
  const code = pending;
  pending = null;
  const r = await rpc('accept_firm_invite', { p_code: code });
  if (!r.ok) { fail('fl-cust-out', r.error ?? ''); return; }
  const out = el('fl-cust-out');
  if (out) out.innerHTML = '<div style="color:var(--grn);font-size:13px;font-weight:700">' + esc(t('fl_ok')) + '</div>';
  const inp = el('fl-code-inp') as HTMLInputElement | null; if (inp) inp.value = '';
  void renderLinks();
}

/* ── Bağlantı listesi — iki tarafta da aynı ───────────────────── */

async function revoke(id: string): Promise<void> {
  const r = await rpc('revoke_firm_link', { p_link_id: id });
  legacy.toast(r.ok ? t('fl_revoked') : (r.error ?? ''));
  void renderLinks();
}

async function renderLinks(): Promise<void> {
  const host = el('fl-links');
  const sb = legacy.sb;
  if (!host || !sb) return;
  const res = await sb.from('firm_clients')
    .select('id,firm_owner_id,client_owner_id,status,linked_at')
    .eq('status', 'active').order('linked_at', { ascending: false });
  const rows: Array<{ id: string; linked_at?: string }> = (res && res.data) || [];
  if (!rows.length) {
    host.innerHTML = '<div style="font-size:12px;color:var(--t2)">' + esc(t('fl_none')) + '</div>';
    return;
  }
  host.innerHTML = rows.map((r) => {
    const d = r.linked_at ? new Date(r.linked_at).toLocaleDateString() : '';
    return '<div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--div)">'
      + '<span style="font-size:11px;font-weight:700;color:var(--grn)">● ' + esc(t('fl_linked')) + '</span>'
      + '<span style="flex:1;font-size:11px;color:var(--t2)">' + esc(d) + '</span>'
      + '<button class="bsm" style="background:var(--rA);color:var(--red);border:1px solid var(--rB)"'
      + ' onclick="flRevoke(\'' + esc(r.id) + '\')">' + esc(t('fl_revoke')) + '</button>'
      + '</div>';
  }).join('');
}

/* ── Kutuları çiz ─────────────────────────────────────────────── */

function isCustomer(): boolean {
  const g = globalThis as unknown as { atIsCustomer?: () => boolean };
  return typeof g.atIsCustomer === 'function' ? g.atIsCustomer() : false;
}

function render(): void {
  const cust = isCustomer();
  const firmBox = el('fl-firm-box');
  const custBox = el('fl-cust-box');

  if (firmBox) {
    firmBox.innerHTML = cust ? '' :
      '<div class="card" style="margin-bottom:12px">'
      + '<div class="set">' + esc(t('fl_firm_t')) + '</div>'
      + '<div style="font-size:11px;color:var(--t2);margin-bottom:10px;line-height:1.6">' + esc(t('fl_firm_d')) + '</div>'
      + '<button class="btn br bfu" onclick="flCreateInvite()">' + esc(t('fl_new')) + '</button>'
      + '<div id="fl-firm-out" style="margin-top:10px"></div>'
      + '<div id="fl-links" style="margin-top:10px"></div>'
      + '</div>';
  }
  if (custBox) {
    custBox.innerHTML = !cust ? '' :
      '<div class="card" style="margin-bottom:12px">'
      + '<div class="set">' + esc(t('fl_cust_t')) + '</div>'
      + '<div style="font-size:11px;color:var(--t2);margin-bottom:10px;line-height:1.6">' + esc(t('fl_cust_d')) + '</div>'
      + '<div style="display:flex;gap:8px">'
      + '<input class="inp" id="fl-code-inp" placeholder="' + esc(t('fl_ph')) + '" style="font-family:monospace;text-transform:uppercase"'
      + ' onkeydown="if(event.key===\'Enter\')flPreview()">'
      + '<button class="btn br bsm" style="flex-shrink:0" onclick="flPreview()">' + esc(t('fl_check')) + '</button>'
      + '</div>'
      + '<div id="fl-cust-out" style="margin-top:10px"></div>'
      + '<div id="fl-links" style="margin-top:10px"></div>'
      + '</div>';
  }
  void renderLinks();

  /* QR ile gelen kodu yakala. Otomatik BAĞLANMAZ — kodu doldurup önizlemeyi
     açar; onay yine kullanıcıdadır. */
  const fromQr = takeHashInvite();
  if (fromQr) {
    const inp = el('fl-code-inp') as HTMLInputElement | null;
    if (inp) { inp.value = fromQr; void preview(); }
  }
}

export default defineFeature({
  id: 'firm-link',
  label: 'Müşteri–firma köprüsü',
  view: null,

  boot: () => { legacy.addTranslations(TEXTS); },

  legacyGlobals: {
    flCreateInvite: () => createInvite(),
    flCopy: (code: unknown) => copy(String(code)),
    flShowQr: (code: unknown) => showQr(String(code)),
    flPreview: () => preview(),
    flAccept: () => accept(),
    flCancel: () => cancel(),
    flRevoke: (id: unknown) => revoke(String(id)),
    flRenderLinks: () => renderLinks(),
    flRender: () => render(),
    flInviteUrl: (code: unknown) => inviteUrl(String(code)),
  },

  owns: { tables: ['firm_invites', 'firm_clients'] },
});
